import instagram from './instagram';
import tiktok from './tiktok';
import youtube from './youtube';
import facebook from './facebook';
import linkedin from './linkedin';
import twitter from './twitter';
import reddit from './reddit';
import twitch from './twitch';

/**
 * @typedef {Object} NetworkConfig
 * @property {string} name - The display name of the social platform.
 * @property {number} sort - The visual priority order for UI selectors and dropdowns (n > 0).
 * @property {string} baseUrl - The base profile URL structure.
 * @property {string[]} domains - Array of all recognized domains for this platform.
 * @property {boolean} supported - Whether the API backend supports this platform (data integration, analytics, etc.). Platforms with UI scaffolding but no backend support should set this to false (e.g., Facebook, X).
 * @property {string} color - The official hex code brand color.
 * @property {boolean} hasVideoContent - Whether this platform's posts are treated as video content (drives video-specific UI such as post cards and metrics). Non-video platforms degrade to generic content rendering.
 * @property {boolean} supportsEmbed - Whether the app has a native in-app embed/player for this platform's posts.
 * @property {RegExp} [profileUrlPattern] - Anchored regex matching a public profile URL for this platform, capturing the handle in group 1 (youtube captures channel id in group 1 or @handle in group 2). Present only for platforms whose profile URLs can be parsed/added by URL; consumed by the URL helpers in util/socialMedia.js. Independent of `supported` (a platform can be addable-by-URL without being a discovery source).
 * @property {boolean} [requiresReferrer] - Whether profile links for this platform must keep the `Referer` header (i.e. not use `rel="noreferrer"` / `noreferrer` window features). LinkedIn gates referrer-less profile visits behind its login wall; consumed by SocialMediaHandle.
 */

/**
 * A dictionary of all available social media platforms and their configurations.
 * @satisfies {Record<string, NetworkConfig>}
 */
export const SOCIAL_NETWORKS = Object.freeze({
  instagram,
  tiktok,
  youtube,
  facebook,
  linkedin,
  twitter,
  reddit,
  twitch,
});

/**
 * @typedef {keyof typeof SOCIAL_NETWORKS} SocialNetworkKey
 */

/**
 * Master list of all registered platform keys, sorted by their priority order.
 * This acts as the base derivation for all system-wide social mappings.
 * @type {Array<SocialNetworkKey>}
 */
export const ALL_NETWORKS = Object.freeze(
  Object.entries(SOCIAL_NETWORKS)
    .sort(([, a], [, b]) => a.sort - b.sort)
    .map(([key]) => key)
);

/**
 * Active subset of platform keys supported by the current frontend application lifecycle.
 * Inherits its sorted order dynamically from ALL_NETWORKS.
 * @type {Array<SocialNetworkKey>}
 */
export const SUPPORTED_NETWORKS = Object.freeze(
  ALL_NETWORKS.filter((key) => SOCIAL_NETWORKS[key].supported)
);
